import React, { useState, useRef, useEffect } from 'react';
import { Search, ShoppingCart, User, Heart, LogOut, Loader2, Home } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../api/auth';
import { API_URL } from '../api';
import { productService } from '../api/products';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../context/AuthContext';
import type { HeaderNavItem } from '../types/headerNav';
import { headerNavApi } from '../api/headerNav';
import HeaderNavDesktop from './header/HeaderNavDesktop';
import HeaderNavMobileBar from './header/HeaderNavMobileBar';

interface HeaderProps {
  cartItemCount?: number;
  wishlistItemCount?: number;
}

const Header: React.FC<HeaderProps> = ({ 
  cartItemCount = 0, 
  wishlistItemCount = 0 
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState<Array<{type: string, title: string, slug?: string, category?: string, brand?: string}>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [navCategories, setNavCategories] = useState<{label: string, slug: string}[]>([]);
  const [headerNavItems, setHeaderNavItems] = useState<HeaderNavItem[]>([]);
  const [storeSettings, setStoreSettings] = useState<{ storeName: string; storeLogo: string }>({ storeName: 'NLS STORE', storeLogo: '' });
  const searchRef = useRef<HTMLDivElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, isAuthenticated, logout: authLogout } = useAuth();

  // Sample search suggestions
  const trendingSearches = ['Shoes', 'Beauty', 'Watches', 'Electronics', 'Handbags'];

  useEffect(() => {
    const loadNav = async () => {
      try {
        const data = await headerNavApi.getPublic();
        setHeaderNavItems(data);
      } catch (error) {
        console.error('Error loading header navigation:', error);
      }
    };
    loadNav();
    const onUpdate = () => loadNav();
    window.addEventListener('headerNavUpdated', onUpdate);
    return () => window.removeEventListener('headerNavUpdated', onUpdate);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
      // Check desktop account ref only (mobile account removed from header)
      if (accountRef.current && !accountRef.current.contains(event.target as Node)) {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Real-time search with product results
  const [searchResults, setSearchResults] = useState<any[]>([]);
  
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchSuggestions([]);
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        // Fetch suggestions
        const suggestionsRes = await productService.getSearchSuggestions(searchQuery, 6);
        if (suggestionsRes.success && suggestionsRes.data) {
          setSearchSuggestions(suggestionsRes.data);
        }
        
        // Fetch actual products for preview (top 4)
        const resultsRes = await productService.searchProducts(searchQuery, 4);
        if (resultsRes.success && resultsRes.data) {
          setSearchResults(resultsRes.data.slice(0, 4));
        }
      } catch (error) {
        console.error('Search error:', error);
        setSearchSuggestions([]);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 150);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    authLogout();
    navigate('/');
  };

  // Fetch store settings from PUBLIC endpoint (no auth needed)
  useEffect(() => {
    const fetchStoreSettings = async () => {
      try {
        console.log('[Header] Fetching store settings from public endpoint...');
        const res = await fetch(`${API_URL}/admin/ultra-settings/public/store`);
        console.log('[Header] API Response status:', res.status);
        const data = await res.json();
        console.log('[Header] API Data:', data);
        
        // DEBUG: Force set a test logo if none exists
        if (!data.data?.storeLogo) {
          console.log('[Header] No logo in DB, using test logo...');
          // Use one of the existing files
          data.data = {
            ...data.data,
            storeLogo: '/uploads/settings/1774874852745-763040275.png',
            storeName: data.data?.storeName || 'NLS STORE'
          };
        }
        
        if (data.success && data.data) {
          let logoUrl = data.data.storeLogo || '';
          console.log('[Header] Raw logo URL from API:', logoUrl);
          // Transform relative URL using centralized utility
          logoUrl = getImageUrl(logoUrl) || '';
          console.log('[Header] Processed logo URL:', logoUrl);
          setStoreSettings({
            storeName: data.data.storeName || 'NLS STORE',
            storeLogo: logoUrl
          });
        } else {
          console.log('[Header] API returned no data or success=false');
        }
      } catch (error) {
        console.error('[Header] Error fetching store settings:', error);
      }
    };
    fetchStoreSettings();
  }, []);

  // Fetch navigation categories from API
  useEffect(() => {
    const fetchNavCategories = async () => {
      try {
        console.log('[Header] Fetching navigation categories...');
        const res = await fetch(`${API_URL}/admin/navigation-categories/public`);
        console.log('[Header] Categories API status:', res.status);
        const data = await res.json();
        console.log('[Header] Categories data:', data);
        if (data.success && data.data) {
          // Handle both array and object formats
          let categoriesArray: any[] = [];
          if (Array.isArray(data.data)) {
            categoriesArray = data.data;
          } else if (data.data.categories && Array.isArray(data.data.categories)) {
            categoriesArray = data.data.categories;
          } else if (typeof data.data === 'object') {
            // Try to find array in the response
            categoriesArray = Object.values(data.data).filter(Array.isArray)[0] || [];
          }
          
          console.log('Categories array:', categoriesArray);
          
          if (categoriesArray.length > 0) {
            console.log(`Setting ${categoriesArray.length} categories`);
            // Process categories - keep Home but ensure it links to root
            const processedCategories = categoriesArray.map((cat: any) => {
              const slug = cat.slug?.toLowerCase() || '';
              const label = cat.label?.trim() || '';
              
              // Skip placeholder/test categories
              if (slug === 'test' || slug === 'undefined' || slug === 'temp' || !label) {
                console.log(`Skipping invalid category: ${label} (${slug})`);
                return null;
              }
              
              // Home should have empty slug to link to root
              if (slug === 'home') {
                return { label: 'Home', slug: '' };
              }
              
              return { label, slug };
            }).filter((cat): cat is { label: string; slug: string } => cat !== null);
            
            setNavCategories(processedCategories);
          } else {
            console.log('No valid categories found, using fallback');
          }
        } else {
          console.log('[Header] No categories from API, using fallback');
          // Fallback to defaults
          setNavCategories([
            { label: 'Home', slug: '' },
            { label: 'Women', slug: 'women' },
            { label: 'Men', slug: 'men' },
            { label: 'Electronics', slug: 'electronics' },
            { label: 'Beauty', slug: 'beauty' },
            { label: 'Watches', slug: 'watches' },
            { label: 'Shoes', slug: 'shoes' },
            { label: 'Accessories', slug: 'accessories' }
          ]);
        }
      } catch (error) {
        console.error('[Header] Error fetching navigation categories:', error);
        console.log('[Header] Using fallback categories due to error');
        // Fallback to defaults
        setNavCategories([
          { label: 'Home', slug: '' },
          { label: 'Women', slug: 'women' },
          { label: 'Men', slug: 'men' },
          { label: 'Electronics', slug: 'electronics' },
          { label: 'Beauty', slug: 'beauty' },
          { label: 'Watches', slug: 'watches' },
          { label: 'Shoes', slug: 'shoes' },
          { label: 'Accessories', slug: 'accessories' }
        ]);
      }
    };
    fetchNavCategories();
  }, []);

  return (
    <>
      {/* Top Utility Bar - Hidden on mobile */}
      <div className="hidden lg:block bg-gray-100 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-2 text-sm">
            <div className="flex items-center gap-4">
              {currentUser ? (
                <>
                  <span className="text-gray-600">Welcome, {currentUser.firstName}!</span>
                  <Link to="/orders" className="text-gray-600 hover:text-primary-600 transition-colors">
                    Track Order
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/help" className="text-gray-600 hover:text-primary-600 transition-colors">
                    Help Center
                  </Link>
                  <Link to="/track-order" className="text-gray-600 hover:text-primary-600 transition-colors">
                    Track Order
                  </Link>
                </>
              )}
            </div>
            
            <div className="flex items-center gap-4">
              {/* Language Selector - Desktop only */}
              {!currentUser && (
                <>
                  <Link to="/login" className="text-gray-600 hover:text-primary-600 transition-colors">
                    Sign In
                  </Link>
                  <Link to="/register" className="text-gray-600 hover:text-primary-600 transition-colors">
                    Register
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation Header - Simplified for mobile */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-3 lg:px-4">
          {/* Top Row: Logo | Cart (mobile) */}
          <div className="flex items-center justify-between h-14 lg:h-16">
            <div className="flex items-center min-w-0 shrink-0">
              <Link to="/" className="flex items-center shrink-0">
                {storeSettings.storeLogo ? (
                  <img 
                    src={storeSettings.storeLogo} 
                    alt={storeSettings.storeName} 
                    className="h-8 lg:h-10 w-auto object-contain"
                    onError={(e) => {
                      console.error('[Header] Logo image failed to load:', storeSettings.storeLogo);
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      setStoreSettings(prev => ({ ...prev, storeLogo: '' }));
                    }}
                  />
                ) : (
                  <span className="text-xl lg:text-2xl font-bold text-primary-600">{storeSettings.storeName}</span>
                )}
              </Link>
            </div>

            {/* Desktop: Search Bar */}
            <div className="hidden lg:flex flex-1 max-w-2xl mx-8" ref={searchRef}>
              <form onSubmit={handleSearch} className="relative w-full">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchOpen(true)}
                    placeholder="Search for products..."
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-primary-600 transition-colors"
                    aria-label="Search"
                  >
                    <Search className="w-5 h-5" />
                  </button>
                </div>

                {/* Search Suggestions Dropdown */}
                {isSearchOpen && (searchQuery.length > 1 || trendingSearches.length > 0) && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[500px] overflow-y-auto">
                    {isSearching && (
                      <div className="p-4 text-center">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" />
                        <p className="text-xs text-gray-500 mt-2">Searching...</p>
                      </div>
                    )}
                    
                    {/* Product Results Preview */}
                    {!isSearching && searchResults.length > 0 && (
                      <div className="p-3 border-b border-gray-100">
                        <p className="text-xs text-gray-500 font-semibold mb-2">TOP RESULTS</p>
                        <div className="space-y-2">
                          {searchResults.map((product) => (
                            <Link
                              key={product._id}
                              to={`/products/${product.slug || product._id}`}
                              onClick={() => {
                                setSearchQuery('');
                                setIsSearchOpen(false);
                              }}
                              className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                            >
                              <img
                                src={product.images?.[0] ? (product.images[0].startsWith('http') ? product.images[0] : `${process.env.REACT_APP_SERVER_URL || 'http://localhost:5001'}${product.images[0]}`) : '/placeholder.svg'}
                                alt={product.name}
                                className="w-12 h-12 object-cover rounded-md bg-gray-100"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/placeholder.svg';
                                }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                                <p className="text-xs text-gray-500">
                                  {typeof product.brand === 'object' ? product.brand?.name : product.brand}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-semibold text-blue-600">
                                  ₹{product.price?.selling || product.price || 0}
                                </p>
                                {product.price?.mrp > product.price?.selling && (
                                  <p className="text-xs text-gray-400 line-through">₹{product.price.mrp}</p>
                                )}
                              </div>
                            </Link>
                          ))}
                        </div>
                        <Link
                          to={`/search?q=${encodeURIComponent(searchQuery)}`}
                          onClick={() => setIsSearchOpen(false)}
                          className="block w-full mt-3 py-2 text-center text-sm text-blue-600 font-medium hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          View all results →
                        </Link>
                      </div>
                    )}
                    
                    {/* Text Suggestions */}
                    {!isSearching && searchSuggestions.length > 0 && (
                      <div className="p-2">
                        <p className="text-xs text-gray-500 font-semibold mb-2 px-2">SUGGESTIONS</p>
                        {searchSuggestions.slice(0, 5).map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setSearchQuery(suggestion.title);
                              navigate(`/search?q=${encodeURIComponent(suggestion.title)}`);
                              setIsSearchOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors text-left"
                          >
                            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="block truncate">{suggestion.title}</span>
                              {suggestion.category && (
                                <span className="text-xs text-gray-500">in {suggestion.category}</span>
                              )}
                            </div>
                            {suggestion.type === 'product' && suggestion.brand && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{suggestion.brand}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* No Results */}
                    {!isSearching && searchQuery.length > 2 && searchResults.length === 0 && searchSuggestions.length === 0 && (
                      <div className="p-4 text-center">
                        <p className="text-sm text-gray-500">No products found</p>
                        <p className="text-xs text-gray-400 mt-1">Try different keywords</p>
                      </div>
                    )}
                    
                    {/* Trending Searches */}
                    <div className="p-2 border-t border-gray-200">
                      <p className="text-xs text-gray-500 font-semibold mb-2 px-2">TRENDING SEARCHES</p>
                      <div className="flex flex-wrap gap-2 px-2">
                        {trendingSearches.map((trend, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setSearchQuery(trend);
                              navigate(`/search?q=${encodeURIComponent(trend)}`);
                              setIsSearchOpen(false);
                            }}
                            className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                          >
                            {trend}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Right: Action Icons - Simplified on mobile */}
            <div className="flex items-center gap-2 lg:gap-4">
              {/* Desktop: Account & Wishlist */}
              <div className="hidden lg:flex items-center gap-4">
                {/* Account */}
                <div className="relative" ref={accountRef}>
                  <button
                    onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                    className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Account menu"
                  >
                    <User className="w-6 h-6" />
                  </button>
                  
                  {isAccountMenuOpen && (
                    <div className="account-dropdown absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-w-[calc(100vw-1rem)] sm:max-w-none origin-top-right">
                      {currentUser ? (
                        <>
                          <div className="px-4 py-3 border-b border-gray-200 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {currentUser.firstName} {currentUser.lastName}
                            </p>
                            <p className="text-sm text-gray-500 truncate" title={currentUser.email}>{currentUser.email}</p>
                          </div>
                          <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                            My Profile
                          </Link>
                          <Link to="/orders" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                            My Orders
                          </Link>
                          <Link to="/wishlist" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                            Wishlist
                          </Link>
                          <Link to="/dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                            Dashboard
                          </Link>
                          <hr className="my-1" />
                          <button
                            onClick={handleLogout}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
                          >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                          </button>
                        </>
                      ) : (
                        <>
                          <Link to="/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                            Sign In
                          </Link>
                          <Link to="/register" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                            Register
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Wishlist */}
                <Link to="/wishlist" className="relative p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Wishlist">
                  <Heart className="w-6 h-6" />
                  {wishlistItemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                      {wishlistItemCount}
                    </span>
                  )}
                </Link>
              </div>

              {/* Cart - Always visible (only icon on mobile) */}
              <Link to="/cart" className="relative p-2 text-gray-700 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Shopping cart">
                <ShoppingCart className="w-6 h-6" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center text-[10px]">
                    {cartItemCount > 99 ? '99+' : cartItemCount}
                  </span>
                )}
              </Link>
            </div>
          </div>

          {/* Mobile: Search + admin header buttons (below search) */}
          <div className="lg:hidden min-w-0 w-full -mx-3 px-0">
            <form onSubmit={handleSearch} className="relative pb-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for products..."
                className="w-full px-4 py-2.5 pr-12 bg-gray-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all text-sm"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                aria-label="Search products"
              >
                <Search className="w-4 h-4" />
              </button>
            </form>
            <HeaderNavMobileBar items={headerNavItems} fallbackCategories={navCategories} />
          </div>
        </div>
      </header>

      <nav className="hidden lg:block bg-white border-b border-gray-200 sticky top-16 z-30">
        <div className="container mx-auto px-4 py-3">
          <HeaderNavDesktop items={headerNavItems} fallbackCategories={navCategories} />
        </div>
      </nav>

    </>
  );
};

export default Header;
